import React, { useEffect, useState } from 'react';
import { LuChartColumn, LuCircleAlert } from 'react-icons/lu';
import { API_BASE_URL } from '../../config.js';
import { Card, CardHeader } from '../ui/Card';
import AdminBadge from '../ui/AdminBadge';
import EmptyState from '../ui/EmptyState';
import Skeleton from '../ui/Skeleton';
import { SpinnerRing } from '../ui/Spinner';
import { WeeklyStatistics } from './WeeklyStatistics';

/** Loads the report statistics (admin) once the dump is available. */
export const StatisticsSection = ({ report, fetchWithAuth, clientSlug, weekId, client }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dumpReady = report?.steps?.dump === 'completed';

  useEffect(() => {
    if (!dumpReady || statistics) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${clientSlug}/week/${weekId}/statistics`);
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok) throw new Error(data?.message);
        setStatistics(data);
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        if (!cancelled) setError('Impossible de charger les statistiques');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dumpReady, fetchWithAuth, clientSlug, weekId, statistics]);

  if (statistics) return <WeeklyStatistics statistics={statistics} client={client} />;

  return (
    <Card>
      <CardHeader icon={LuChartColumn} title="Statistiques de la semaine" badge={<AdminBadge />} />
      {error ? (
        <EmptyState icon={LuCircleAlert} tone="red" title={error} description="Réessayez en rechargeant la page." />
      ) : !dumpReady ? (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <SpinnerRing label="Récupération des données" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Les statistiques seront calculées dès la fin de la récupération des données.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 p-4 sm:p-5 lg:grid-cols-4" aria-busy={loading}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
      )}
    </Card>
  );
};

export default StatisticsSection;
